import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CONTACT, whatsappLink } from "@/constants";
import { ENQUIRY_TYPES } from "@/components/enquiry/enquiryConfig";
import { PageShell } from "@/components/PageShell";

export const inputClass =
  "w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors duration-300";
export const labelClass = "block text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2";

export default function EnquiryPage() {
  const { type } = useParams();
  const [params] = useSearchParams();
  const config = ENQUIRY_TYPES[type];
  const [details, setDetails] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", phone: "", whatsapp_ok: false, consent: false, message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const listingId = params.get("listing");
  const listingTitle = params.get("title");

  useEffect(() => {
    const preset = {};
    params.forEach((v, k) => {
      if (!["listing", "title"].includes(k)) preset[k] = v;
    });
    if (listingTitle) preset.model = listingTitle;
    setDetails(preset);
  }, [params, listingTitle]);

  if (!config) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.consent) return toast.error("Please confirm you are happy for us to contact you.");
    setSubmitting(true);
    try {
      const res = await api("/leads", {
        method: "POST",
        body: {
          type,
          source: "form",
          name: contact.name,
          email: contact.email,
          phone: contact.phone || null,
          whatsapp_ok: contact.whatsapp_ok,
          consent: true,
          details: { ...details, ...(listingTitle ? { listing: listingTitle } : {}) },
          message: contact.message || null,
          listing_id: listingId,
        },
      });
      setDone(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <section className="relative pt-32 pb-24 min-h-screen">
        <div className="absolute inset-0">
          <img src={config.image} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/80 to-obsidian" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">{config.overline}</p>
            <h1 className="font-serif font-light text-4xl sm:text-5xl text-white tracking-tight leading-tight" data-testid="enquiry-page-title">{config.title}</h1>
            <p className="mt-6 text-base text-white/70 font-light leading-relaxed">{config.intro}</p>
            {listingTitle && (
              <p className="mt-6 text-sm text-gold/90 border-l border-gold pl-4" data-testid="enquiry-listing-context">Regarding: {listingTitle}</p>
            )}
            <div className="mt-10 space-y-3 text-sm text-white/60">
              <a href={CONTACT.phoneTel} className="flex items-center gap-3 hover:text-gold transition-colors" data-testid="enquiry-call-link"><Phone size={14} /> {CONTACT.phoneDisplay}</a>
              <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-gold transition-colors" data-testid="enquiry-whatsapp-link"><MessageCircle size={14} /> WhatsApp {CONTACT.whatsappDisplay}</a>
            </div>
          </motion.div>

          <div className="lg:col-span-8">
            {done ? (
              <ThankYou result={done} />
            ) : (
              <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} onSubmit={submit}
                           data-testid={`enquiry-form-${type}`} className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-black/40 border border-white/10 backdrop-blur-xl p-8 lg:p-10 rounded-sm">
                <p className="sm:col-span-2 text-[10px] uppercase tracking-[0.25em] text-gold">1 · Your requirement</p>
                {config.fields.map((f) => (
                  <Field key={f.key} field={f} value={details[f.key] ?? ""} onChange={(v) => setDetails((d) => ({ ...d, [f.key]: v }))} />
                ))}
                <div className="sm:col-span-2">
                  <label className={labelClass}>Notes</label>
                  <textarea rows={3} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} data-testid="enquiry-notes-input"
                            className={`${inputClass} resize-none`} placeholder="Anything else we should know" />
                </div>
                <p className="sm:col-span-2 text-[10px] uppercase tracking-[0.25em] text-gold mt-4">2 · Your details</p>
                <div>
                  <label className={labelClass}>Name</label>
                  <input required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className={inputClass} placeholder="Your full name" data-testid="enquiry-name-input" />
                </div>
                <div>
                  <label className={labelClass}>Email</label>
                  <input required type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={inputClass} placeholder="you@company.com" data-testid="enquiry-email-input" />
                </div>
                <div>
                  <label className={labelClass}>Phone</label>
                  <input required type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className={inputClass} placeholder="+44 ..." data-testid="enquiry-phone-input" />
                </div>
                <label className="flex items-center gap-3 text-sm text-white/60 self-end pb-3 cursor-pointer">
                  <input type="checkbox" checked={contact.whatsapp_ok} onChange={(e) => setContact({ ...contact, whatsapp_ok: e.target.checked })} className="accent-[#C5A059]" data-testid="enquiry-whatsapp-checkbox" />
                  Happy to be contacted on WhatsApp
                </label>
                <label className="sm:col-span-2 flex items-start gap-3 text-xs text-white/50 cursor-pointer">
                  <input type="checkbox" checked={contact.consent} onChange={(e) => setContact({ ...contact, consent: e.target.checked })} className="mt-0.5 accent-[#C5A059]" data-testid="enquiry-consent-checkbox" />
                  I agree to Aero Vista Club contacting me about this enquiry. We never share your details.
                </label>
                <div className="sm:col-span-2">
                  <button type="submit" disabled={submitting} data-testid="enquiry-submit-button"
                          className="group inline-flex items-center gap-3 bg-gold text-black text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-sm transition-colors duration-300 hover:bg-gold-light disabled:opacity-50">
                    {submitting ? "Sending..." : "Send request"}
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </motion.form>
            )}
          </div>
        </div>
      </section>
    </PageShell>
  );
}

const Field = ({ field, value, onChange }) => (
  <div>
    <label className={labelClass}>{field.label}</label>
    {field.type === "select" ? (
      <select required={field.required} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputClass} appearance-none`} data-testid={`enquiry-field-${field.key}`}>
        <option value="" className="bg-surface">Select...</option>
        {field.options.map((o) => <option key={o} value={o} className="bg-surface text-white">{o}</option>)}
      </select>
    ) : (
      <input required={field.required} type={field.type || "text"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder}
             className={inputClass} data-testid={`enquiry-field-${field.key}`} />
    )}
  </div>
);

export const ThankYou = ({ result }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="enquiry-thank-you"
              className="bg-black/40 border border-gold/40 backdrop-blur-xl p-10 lg:p-14 rounded-sm">
    <div className="w-12 h-12 rounded-full bg-gold/15 border border-gold/50 flex items-center justify-center text-gold mb-8"><Check size={20} /></div>
    <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Request received</p>
    <h2 className="font-serif font-light text-3xl sm:text-4xl text-white">Thank you. The team will respond within 2 hours during business hours.</h2>
    <p className="mt-6 text-white/60 text-sm">Your reference is <span className="text-white font-medium" data-testid="enquiry-reference">{result.reference}</span>. A confirmation has been emailed to you.</p>
    <div className="mt-10 flex flex-wrap gap-4">
      <a href={whatsappLink(`Hello, my Aero Vista Club reference is ${result.reference}.`)} target="_blank" rel="noopener noreferrer" data-testid="thank-you-whatsapp-button"
         className="inline-flex items-center gap-2 bg-[#25D366] text-black text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-sm hover:brightness-110 transition"><MessageCircle size={14} /> Chat on WhatsApp</a>
      <a href={CONTACT.phoneTel} data-testid="thank-you-call-button"
         className="inline-flex items-center gap-2 border border-white/20 text-white text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-sm hover:border-gold hover:text-gold transition-colors"><Phone size={14} /> Call {CONTACT.phoneDisplay}</a>
      <Link to="/" className="inline-flex items-center text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white px-2 py-3.5 transition-colors" data-testid="thank-you-home-link">Back to site</Link>
    </div>
  </motion.div>
);
